// @code-guard: skip-file reason="Large legacy chart container will be split and tokenized in follow-up refactor"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FC, ReactNode, RefObject } from 'react';
import { Global, css } from '@emotion/react';
import { Slider, Spin } from 'antd';

import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import NoDataForSelection from 'components/atoms/NoDataForSelection';
import { colorConstants } from 'config/colorConstants';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import type { AxisGroup, KpiItem, PricePackBubbleRow, SegmentItem } from 'hooks/ppa/useFetchPricePackArchitecture';
import { formatNumWithKMBAlt } from 'utils/helperMethods';
import BubbleScatterChart from './BubbleScatterChart';
import HeaderSortDescChevron from 'assets/svg/HeaderSortDescChevron';
import {
    getPackArchitectureEntityKey,
    formatPackArchitectureKpiValue,
    EMPTY_CELL_VALUE,
} from './packArchitectureTable.helpers';
import InboxOutlined from 'assets/svg/InboxOutlined';

type SegmentLegend = {
    id: string;
    label: string;
    color: string;
};

const LEGEND_COLOR_DOT_SIZE_PX = 8;
const DEFAULT_BUBBLE_COLOR = colorConstants.packArchitectureBubbleDefault;
/** API sentinel for the consolidated "Other Vendor" node (carries the `members` list). */
const OTHER_GROUP_EX_ID = '__other__';
/** Figma: "Other" group chips use a neutral grey dot. */
const OTHER_SEGMENT_DOT_COLOR = '#d8dadf';
/** Figma: vertical divider between regular legend chips and the "Other" group. */
const LEGEND_DIVIDER_COLOR = '#d9d9d9';
const isOtherGroupExId = (exId?: string): boolean => (exId ?? '').trim().toLowerCase() === OTHER_GROUP_EX_ID;
/** Returns true for segment labels that represent a catch-all "Other" bucket (e.g. "Other", "Others"). */
const isOtherLikeLabel = (label: string): boolean => {
    const lower = label.trim().toLowerCase();
    return lower === 'other' || lower === 'others';
};

type LegendChipProps = {
    segment: SegmentLegend;
    isActive: boolean;
    isDimmed: boolean;
    truncate: boolean;
    onClick?: () => void;
    measureOnly?: boolean;
    displayOnly?: boolean;
};

const LegendColorDot = ({ color }: { color: string }) => (
    <span
        aria-hidden
        className="shrink-0"
        style={{
            display: 'inline-block',
            width: LEGEND_COLOR_DOT_SIZE_PX,
            height: LEGEND_COLOR_DOT_SIZE_PX,
            minWidth: LEGEND_COLOR_DOT_SIZE_PX,
            minHeight: LEGEND_COLOR_DOT_SIZE_PX,
            borderRadius: '50%',
            backgroundColor: color || DEFAULT_BUBBLE_COLOR,
            boxSizing: 'border-box',
            flexShrink: 0,
        }}
    />
);

const LegendChip: FC<LegendChipProps> = ({
    segment,
    isActive,
    isDimmed,
    truncate,
    onClick,
    measureOnly = false,
    displayOnly = false,
}) => {
    const interactive = !measureOnly && !displayOnly;
    return (
        <button
            type="button"
            data-legend-chip={measureOnly ? '' : undefined}
            onClick={interactive ? onClick : undefined}
            tabIndex={interactive ? 0 : -1}
            aria-disabled={displayOnly || undefined}
            className={`m-0 flex shrink-0 items-center gap-1.5 border-0 bg-transparent p-0 text-xs leading-none transition-opacity ${
                interactive ? 'cursor-pointer' : 'cursor-default'
            } ${truncate ? 'max-w-[140px] whitespace-nowrap' : 'max-w-none'} ${
                isActive ? 'font-semibold text-toolbarLabelGrey' : 'text-toolbarLabelGrey'
            }`}
            style={{ opacity: isDimmed ? 0.45 : 1 }}
            title={segment.label}
        >
            <LegendColorDot color={segment.color} />
            <span className={truncate ? 'truncate' : ''}>{segment.label}</span>
        </button>
    );
};

const countLegendChipsThatFit = (chipWidths: number[], rowWidth: number, moreButtonWidth: number): number => {
    if (chipWidths.length === 0) return 0;

    const totalWidth = chipWidths.reduce((sum, width, index) => sum + width + (index > 0 ? LEGEND_CHIP_GAP_PX : 0), 0);
    if (totalWidth <= rowWidth) return chipWidths.length;

    const available = rowWidth - moreButtonWidth - LEGEND_CHIP_GAP_PX;
    let used = 0;
    let count = 0;

    for (let index = 0; index < chipWidths.length; index++) {
        const chipWidth = chipWidths[index] + (index > 0 ? LEGEND_CHIP_GAP_PX : 0);
        if (count > 0 && used + chipWidth > available) break;
        used += chipWidth;
        count++;
    }

    return Math.max(1, count);
};

export type PackArchitectureChartWithControlsProps = {
    chartTitle: ReactNode;
    xAxisTitle: string;
    yAxisTitle: string;
    horizontalScrollRef?: RefObject<HTMLDivElement | null>;
    loading?: boolean;
    showError?: boolean;
    errorMessage?: string;
};

const VISIBLE_LEGEND_LIMIT = 10;
const LEGEND_CHIP_GAP_PX = 12;
const LEGEND_MORE_LABEL_WIDTH_PX = 72;
const LEGEND_DROPDOWN_BUTTON_WIDTH_PX = 24;
const LEGEND_RIGHT_CONTROLS_GAP_PX = 8;
/** Expanded legend: multi-row wrap with vertical scroll (Figma). */
const LEGEND_EXPANDED_MAX_HEIGHT_PX = 128;
const DEFAULT_BUBBLE_RADIUS = 10;
const DEFAULT_Y_MIN = 0;
const DEFAULT_Y_MAX = 100;
const MIN_X_AXIS_COL_GAP_PX = 120;
const MIN_CHART_BASE_WIDTH_PX = 760;
const MIN_INITIAL_SCROLLABLE_EXTRA_WIDTH_PX = 240;
const PAGINATION_RIGHT_EDGE_THRESHOLD_PX = 16;
const PAGINATION_SCROLL_RETREAT_PX = 180;
const CHART_DECIMAL_PRECISION = 1;
const Y_ZOOM_SLIDER_STEP = Math.pow(10, -CHART_DECIMAL_PRECISION);

type ChartBubbleRow = {
    key: string;
    columnKey: string;
    segmentKey: string;
    columnLabel: string;
    segmentLabel: string;
    xFieldValue: number;
    yFieldValue: number;
    bubbleSizeValue: number;
    bubbleColor: string;
    segmentBaseColor: string;
    valueText: string;
    avgText: string;
    volumeSalesText: string;
    growthText: string;
    growthPercentage: number | null;
    valueAverageLabel: string;
    growthDirection: 'up' | 'down' | 'neutral';
    tooltipKpis: Array<{
        id: string;
        label: string;
        value: string;
        growthText?: string;
        trendDirection?: string;
    }>;
    bubbleLabel: string;
    highlightLabel: string;
    isPinpointSelected: boolean;
};

const getKpi = (segment: SegmentItem, kpiId: string): KpiItem | undefined =>
    (segment.kpis ?? []).find((kpi) => String(kpi.kpi_id).toLowerCase() === kpiId.toLowerCase());
const NON_TOOLTIP_KPI_IDS = new Set(['color_code', 'radius']);

const toChartKpiDisplay = (kpi: KpiItem | undefined, kpiId: string, yAxisKpiId: string): string => {
    if (!kpi) return '-';
    const formatted = formatPackArchitectureKpiValue(kpi, kpiId, yAxisKpiId);
    return formatted === EMPTY_CELL_VALUE ? '-' : formatted;
};

const formatRangeLabel = (value: number) => value.toFixed(CHART_DECIMAL_PRECISION);
const formatRoundedValue = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '-';
    return formatNumWithKMBAlt(value, CHART_DECIMAL_PRECISION, CHART_DECIMAL_PRECISION);
};
const formatRoundedGrowth = (growthPercentage?: number) => {
    if (typeof growthPercentage !== 'number' || Number.isNaN(growthPercentage)) return '-';
    const sign = growthPercentage > 0 ? '+' : growthPercentage < 0 ? '-' : '';
    return `${sign}${Math.abs(growthPercentage).toFixed(CHART_DECIMAL_PRECISION)}%`;
};
const buildValueAverageLabel = (valueText: string, avgText: string) =>
    `${HEAT_MAP_LABEL_HEADER}\n${valueText} | ${avgText}`;
const getGrowthDirection = (growthPercentage?: number): 'up' | 'down' | 'neutral' => {
    if (typeof growthPercentage !== 'number' || Number.isNaN(growthPercentage)) return 'neutral';
    if (growthPercentage > 0) return 'up';
    if (growthPercentage < 0) return 'down';
    return 'neutral';
};
const getHeatMapBubbleColor = (direction: 'up' | 'down' | 'neutral'): string => {
    if (direction === 'up') return colorConstants.growthUp;
    if (direction === 'down') return colorConstants.growthDown;
    return colorConstants.greyAlt15;
};
/** Figma heat-map segment band uses attention green/red, not the bubble arrow greens. */
const getHeatMapSegmentHighlightColor = (direction: 'up' | 'down' | 'neutral'): string => {
    if (direction === 'up') return '#00A63E';
    if (direction === 'down') return colorConstants.growthDown;
    return colorConstants.greyAlt15;
};
const HEAT_MAP_HIGHLIGHT_OPACITY = 0.07;
const CHART_CONTAINER_ID = 'pack-architecture-bubble-chart-2';
const CHART_PANEL_CLASS = 'rounded-md border border-borderLinePurpuleAlt bg-white p-4';
/** Matches the chart row container so loader, empty, and data states share the same height. */
const CHART_AREA_HEIGHT_CLASS = 'h-[520px]';
const HEAT_MAP_LABEL_HEADER = 'Value   | Avg.';
const Y_ZOOM_SLIDER_CLASS = 'pack-architecture-y-zoom-slider';
const Y_ZOOM_SLIDER_LABEL_CLASS = 'w-[24px] shrink-0 text-center text-[10px] leading-[14px] text-[#636A79]';

const yZoomSliderTooltipStyles = css`
    .${Y_ZOOM_SLIDER_CLASS} .ant-tooltip.ant-slider-tooltip .ant-tooltip-inner {
        min-width: auto;
        min-height: auto;
        padding: 4px 10px;
        background-color: #3f3f46;
        color: #ffffff;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        border-radius: 8px;
        box-shadow: none;
    }

    .${Y_ZOOM_SLIDER_CLASS} .ant-tooltip.ant-slider-tooltip .ant-tooltip-arrow {
        display: none;
    }

    .${Y_ZOOM_SLIDER_CLASS} .ant-tooltip.ant-slider-tooltip .ant-tooltip-arrow-content {
        display: none;
    }

    .${Y_ZOOM_SLIDER_CLASS}.${Y_ZOOM_SLIDER_CLASS}--disabled {
        opacity: 0.45;
        pointer-events: none;
    }
`;

type VerticalRangeSliderProps = {
    min: number;
    max: number;
    value: [number, number];
    onChange: (nextRange: [number, number]) => void;
    formatLabel: (value: number) => string;
    step?: number;
    disabled?: boolean;
};

const VerticalRangeSlider: FC<VerticalRangeSliderProps> = ({
    min,
    max,
    value,
    onChange,
    formatLabel,
    step = Y_ZOOM_SLIDER_STEP,
    disabled = false,
}) => {
    const previousValueRef = useRef(value);
    const sliderContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        previousValueRef.current = value;
    }, [value]);

    const handleRangeChange = (nextRange: number | [number, number]) => {
        if (disabled) return;
        if (!Array.isArray(nextRange) || nextRange.length !== 2) return;

        let low = Math.min(nextRange[0], nextRange[1]);
        let high = Math.max(nextRange[0], nextRange[1]);
        const [prevLow, prevHigh] = previousValueRef.current;
        const lowMoved = Math.abs(low - prevLow) >= step / 2;
        const highMoved = Math.abs(high - prevHigh) >= step / 2;

        if (high - low < step) {
            if (lowMoved && !highMoved) {
                low = Math.max(min, high - step);
            } else if (highMoved && !lowMoved) {
                high = Math.min(max, low + step);
            } else {
                const midpoint = (low + high) / 2;
                low = Math.max(min, midpoint - step / 2);
                high = Math.min(max, midpoint + step / 2);
                if (high - low < step) {
                    if (high < max) {
                        high = Math.min(max, low + step);
                    } else {
                        low = Math.max(min, high - step);
                    }
                }
            }
        }

        if (low >= high) return;

        const nextValue: [number, number] = [low, high];
        previousValueRef.current = nextValue;
        onChange(nextValue);
    };

    const formatTooltipValue = useCallback(
        (val?: number) => formatLabel(typeof val === 'number' ? val : min),
        [formatLabel, min],
    );

    return (
        <>
            <Global styles={yZoomSliderTooltipStyles} />
            <div className="flex h-full w-[36px] flex-col items-end justify-center gap-1">
                <span className={Y_ZOOM_SLIDER_LABEL_CLASS}>{formatLabel(max)}</span>
                <div
                    ref={sliderContainerRef}
                    className={`relative w-[28px] flex-1 overflow-visible ${Y_ZOOM_SLIDER_CLASS}${
                        disabled ? ` ${Y_ZOOM_SLIDER_CLASS}--disabled` : ''
                    }`}
                    style={{ marginTop: 0, marginBottom: 12 }}
                >
                    <Slider
                        vertical
                        range
                        disabled={disabled}
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        // @ts-expect-error rc-slider range props are omitted from antd Slider types
                        allowCross={false}
                        pushable={step}
                        onChange={handleRangeChange}
                        className="absolute left-[0px] z-10 m-0 h-full w-[12px]"
                        style={{ padding: 0 }}
                        railStyle={{
                            backgroundColor: '#ECE9F5',
                            width: 12,
                            borderRadius: 9999,
                            padding: 0,
                        }}
                        trackStyle={[
                            {
                                backgroundColor: '#E6E0F5',
                                width: 12,
                                borderRadius: 9999,
                            },
                        ]}
                        handleStyle={[
                            {
                                width: 34,
                                height: 12,
                                marginLeft: -11,
                                marginTop: 0,
                                borderRadius: 9999,
                                border: '1px solid #A6A6B0',
                                backgroundColor: '#fff',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            },
                            {
                                width: 34,
                                height: 12,
                                marginLeft: -11,
                                marginTop: 0,
                                borderRadius: 9999,
                                border: '1px solid #A6A6B0',
                                backgroundColor: '#fff',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            },
                        ]}
                        tooltip={{
                            formatter: formatTooltipValue,
                            placement: 'top',
                            getPopupContainer: () => sliderContainerRef.current ?? document.body,
                        }}
                    />
                </div>
                <span className={Y_ZOOM_SLIDER_LABEL_CLASS}>{formatLabel(min)}</span>
            </div>
        </>
    );
};

// @code-guard: skip-next-function reason="Large legacy chart+controls component; scheduled extraction into smaller tokenized sections"
const PackArchitectureChartWithControls: FC<PackArchitectureChartWithControlsProps> = ({
    chartTitle,
    xAxisTitle,
    yAxisTitle,
    horizontalScrollRef: horizontalScrollRefProp,
    loading = false,
    showError = false,
    errorMessage = 'Insufficient data',
}) => {
    const {
        rows,
        yAxis,
        bubbleSize,
        showHeatMap,
        isChartLoading: contextLoading,
        isChartPaginatedLoading,
        isChartPanelLoading,
        isChartError: contextIsError,
        chartError: contextError,
        hasMorePages,
        fetchNextPricePackArchitecturePage,
        activeSegmentKey,
        selectedColumnKey,
        selectedBubbleKey,
        toggleSegmentSelection,
        selectBubble,
        clearSelection,
    } = usePackArchitectureContext();
    const [isLegendExpanded, setIsLegendExpanded] = useState(false);
    const [isOtherVendorExpanded, setIsOtherVendorExpanded] = useState(false);
    const [visibleChipCount, setVisibleChipCount] = useState(VISIBLE_LEGEND_LIMIT);
    const legendContainerRef = useRef<HTMLDivElement>(null);
    const legendRowRef = useRef<HTMLDivElement>(null);
    const legendMeasureRef = useRef<HTMLDivElement>(null);
    const legendControlsMeasureRef = useRef<HTMLDivElement>(null);
    const legendMoreMeasureRef = useRef<HTMLSpanElement>(null);
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
    const internalHorizontalScrollRef = useRef<HTMLDivElement | null>(null);
    const horizontalScrollRef = horizontalScrollRefProp ?? internalHorizontalScrollRef;
    const isFetchingNextPageRef = useRef(false);
    const scrollSnapshotBeforePaginationRef = useRef<{ scrollLeft: number; scrollWidth: number } | null>(null);
    const chartRows = useMemo<ChartBubbleRow[]>(
        () =>
            (rows ?? []).flatMap((column: PricePackBubbleRow, columnIndex: number) =>
                (column.data ?? []).flatMap((axisItem: AxisGroup) =>
                    (axisItem.data ?? []).map((segment: SegmentItem, segmentIndex: number) => {
                        const yAxisKpi = getKpi(segment, yAxis);
                        const bubbleSizeKpi = getKpi(segment, bubbleSize);
                        const colorKpi = getKpi(segment, 'color_code');
                        const radiusKpi = getKpi(segment, 'radius');
                        const yValue =
                            typeof yAxisKpi?.raw_value === 'number'
                                ? yAxisKpi.raw_value
                                : Number(yAxisKpi?.value ?? 0) || 0;
                        const radiusValue =
                            typeof radiusKpi?.raw_value === 'number'
                                ? radiusKpi.raw_value
                                : Number(radiusKpi?.value ?? DEFAULT_BUBBLE_RADIUS) || DEFAULT_BUBBLE_RADIUS;
                        const selectedGrowthPercentage =
                            typeof bubbleSizeKpi?.growth_percentage === 'number'
                                ? bubbleSizeKpi.growth_percentage
                                : yAxisKpi?.growth_percentage;
                        const growthValue = formatRoundedGrowth(selectedGrowthPercentage);
                        const growthDirection = getGrowthDirection(selectedGrowthPercentage);
                        const bubbleColor = colorKpi?.value || DEFAULT_BUBBLE_COLOR;
                        const xBase = columnIndex + 1;
                        const columnKey = getPackArchitectureEntityKey(column);
                        const segmentKey = getPackArchitectureEntityKey(segment);
                        const valueText = bubbleSizeKpi
                            ? toChartKpiDisplay(bubbleSizeKpi, bubbleSize, yAxis)
                            : formatRoundedValue(radiusValue);
                        const avgText = toChartKpiDisplay(yAxisKpi, yAxis, yAxis);
                        const valueAverageLabel = buildValueAverageLabel(valueText, avgText);
                        const tooltipKpis = (segment.kpis ?? [])
                            .filter((kpi) => !NON_TOOLTIP_KPI_IDS.has(String(kpi.kpi_id).toLowerCase()))
                            .map((kpi) => ({
                                id: kpi.kpi_id,
                                label: kpi.display_label || kpi.kpi_name || kpi.kpi_id,
                                value: toChartKpiDisplay(kpi, kpi.kpi_id, yAxis),
                                growthText: formatRoundedGrowth(kpi.growth_percentage),
                                trendDirection: kpi.trend_direction,
                            }));

                        return {
                            key: `${columnKey}_${axisItem.id}_${segmentKey}_${segmentIndex}`,
                            columnKey,
                            segmentKey,
                            columnLabel: column.description,
                            segmentLabel: segment.description,
                            xFieldValue: xBase,
                            yFieldValue: yValue,
                            bubbleSizeValue: radiusValue,
                            bubbleColor,
                            segmentBaseColor: bubbleColor,
                            valueText,
                            avgText,
                            valueAverageLabel,
                            volumeSalesText: valueText,
                            growthText: growthValue,
                            growthPercentage:
                                typeof selectedGrowthPercentage === 'number' && !Number.isNaN(selectedGrowthPercentage)
                                    ? selectedGrowthPercentage
                                    : null,
                            growthDirection,
                            tooltipKpis,
                            bubbleLabel: '',
                            highlightLabel: '',
                            isPinpointSelected: false,
                        };
                    }),
                ),
            ),
        [bubbleSize, rows, yAxis],
    );

    const activeSegmentGrowthMeta = useMemo(() => {
        if (!activeSegmentKey || !showHeatMap) return null;

        const segmentRows = chartRows.filter((row) => row.segmentKey === activeSegmentKey);
        const validGrowths = segmentRows
            .map((row) => row.growthPercentage)
            .filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

        if (validGrowths.length === 0) {
            return {
                direction: 'neutral' as const,
                color: getHeatMapSegmentHighlightColor('neutral'),
                avgGrowth: null,
                avgGrowthText: '-',
            };
        }

        const avgGrowth = validGrowths.reduce((sum, value) => sum + value, 0) / validGrowths.length;
        const direction = getGrowthDirection(avgGrowth);

        return {
            direction,
            color: getHeatMapSegmentHighlightColor(direction),
            avgGrowth,
            avgGrowthText: formatRoundedGrowth(avgGrowth),
        };
    }, [activeSegmentKey, chartRows, showHeatMap]);

    const segmentHighlightConfig = useMemo(() => {
        if (!showHeatMap || !activeSegmentKey || !activeSegmentGrowthMeta) return undefined;

        return {
            mode: 'heatMap' as const,
            fillColor: activeSegmentGrowthMeta.color,
            fillOpacity: HEAT_MAP_HIGHLIGHT_OPACITY,
            lineColor: activeSegmentGrowthMeta.color,
            useSegmentAvgLabel: true,
            avgGrowthText: activeSegmentGrowthMeta.avgGrowthText,
        };
    }, [activeSegmentGrowthMeta, activeSegmentKey, showHeatMap]);

    const yAxisBounds = useMemo(() => {
        if (chartRows.length === 0) return { min: DEFAULT_Y_MIN, max: DEFAULT_Y_MAX };
        const values = chartRows.map((row) => row.yFieldValue);
        const max = Math.max(...values);
        if (max <= 0) return { min: DEFAULT_Y_MIN, max: 1 };
        const pad = max * 0.1;
        return { min: DEFAULT_Y_MIN, max: max + pad };
    }, [chartRows]);

    const isYZoomDisabled = chartRows.length <= 1;

    const [yZoomRange, setYZoomRange] = useState<[number, number]>([yAxisBounds.min, yAxisBounds.max]);

    useEffect(() => {
        setYZoomRange([yAxisBounds.min, yAxisBounds.max]);
    }, [yAxisBounds.max, yAxisBounds.min]);

    useEffect(() => {
        if (!isYZoomDisabled) return;
        setYZoomRange([yAxisBounds.min, yAxisBounds.max]);
    }, [isYZoomDisabled, yAxisBounds.max, yAxisBounds.min]);

    useEffect(() => {
        if (!selectedColumnKey || !horizontalScrollRef.current) return;

        const columnIndex = (rows ?? []).findIndex((row) => getPackArchitectureEntityKey(row) === selectedColumnKey);
        if (columnIndex === -1) return;

        const container = horizontalScrollRef.current;
        const targetLeft = Math.max(0, columnIndex * MIN_X_AXIS_COL_GAP_PX - container.clientWidth / 2);
        container.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }, [rows, selectedColumnKey]);

    useEffect(() => {
        if (!activeSegmentKey) return;
        setYZoomRange([yAxisBounds.min, yAxisBounds.max]);
    }, [activeSegmentKey, selectedColumnKey, yAxisBounds.max, yAxisBounds.min]);

    const regularSegmentLegends = useMemo<SegmentLegend[]>(() => {
        const map = new Map<string, { label: string; color: string }>();
        chartRows.forEach((row) => {
            if (!map.has(row.segmentKey)) {
                map.set(row.segmentKey, {
                    label: row.segmentLabel,
                    color: row.segmentBaseColor,
                });
            }
        });
        return Array.from(map.entries())
            .map(([id, { label, color }]) => ({ id, label, color }))
            .sort((a, b) => {
                const aOther = isOtherLikeLabel(a.label);
                const bOther = isOtherLikeLabel(b.label);
                if (aOther === bOther) return 0;
                return aOther ? 1 : -1;
            });
    }, [chartRows]);

    /** The `__other__` ("Other Vendor") node consolidates low-volume vendors; show its members. */
    const otherVendorLegend = useMemo(() => {
        const node = (rows ?? []).find((row: PricePackBubbleRow) => isOtherGroupExId(row.ex_id));
        if (!node) return null;

        const memberChips: SegmentLegend[] = (node.members ?? [])
            .map((member) => member.description?.trim() || member.ex_id?.trim() || '')
            .filter((name) => name.length > 0)
            .map((name, index) => ({
                id: `${OTHER_GROUP_EX_ID}-member-${index}`,
                label: name,
                color: OTHER_SEGMENT_DOT_COLOR,
            }));

        if (memberChips.length === 0) return null;

        return {
            // Same selection key the chart bubbles carry for this column, so clicking the
            // label toggles/highlights the Other Vendor column like a normal legend chip.
            key: getPackArchitectureEntityKey(node),
            label: node.description?.trim() || 'Other Vendor',
            chips: memberChips,
        };
    }, [rows]);

    const visibleSegments = useMemo(() => {
        const limit = Math.min(visibleChipCount, regularSegmentLegends.length);
        const defaultVisibleSegments = regularSegmentLegends.slice(0, limit);
        if (!activeSegmentKey) return defaultVisibleSegments;

        const activeIndex = regularSegmentLegends.findIndex((segment) => segment.id === activeSegmentKey);
        if (activeIndex === -1 || activeIndex < limit) return defaultVisibleSegments;

        const activeSegment = regularSegmentLegends[activeIndex];
        // "Other"-like segments always live at the end; never evict a regular chip to show them.
        if (isOtherLikeLabel(activeSegment.label)) return defaultVisibleSegments;

        const visibleWithoutLast = defaultVisibleSegments.slice(0, Math.max(0, limit - 1));
        return [...visibleWithoutLast, activeSegment];
    }, [activeSegmentKey, regularSegmentLegends, visibleChipCount]);

    // otherVendorLegend is always placed last; it's hidden in collapsed mode so add 1 to the overflow count.
    const hasLegendOverflow = regularSegmentLegends.length > visibleChipCount || !!otherVendorLegend;
    const hiddenLegendCount =
        Math.max(0, regularSegmentLegends.length - visibleChipCount) + (otherVendorLegend ? 1 : 0);

    useLayoutEffect(() => {
        if (isLegendExpanded) return undefined;

        const container = legendContainerRef.current;
        const measure = legendMeasureRef.current;
        if (!container || !measure || regularSegmentLegends.length === 0) {
            setVisibleChipCount(regularSegmentLegends.length);
            return undefined;
        }

        const updateVisibleChipCount = () => {
            const chips = measure.querySelectorAll('[data-legend-chip]');
            const chipWidths = Array.from(chips).map((chip) => (chip as HTMLElement).offsetWidth);
            const moreLabelWidth = legendMoreMeasureRef.current?.offsetWidth ?? LEGEND_MORE_LABEL_WIDTH_PX;
            const dropdownWidth = legendControlsMeasureRef.current?.querySelector('[data-legend-dropdown-measure]')
                ? (legendControlsMeasureRef.current.querySelector('[data-legend-dropdown-measure]') as HTMLElement)
                      .offsetWidth
                : LEGEND_DROPDOWN_BUTTON_WIDTH_PX;
            const reservedRightWidth =
                (legendControlsMeasureRef.current?.offsetWidth ??
                    moreLabelWidth + dropdownWidth + LEGEND_RIGHT_CONTROLS_GAP_PX) + LEGEND_RIGHT_CONTROLS_GAP_PX;
            const availableChipWidth = Math.max(0, container.clientWidth - reservedRightWidth);
            const nextCount = countLegendChipsThatFit(chipWidths, availableChipWidth, 0);
            setVisibleChipCount(Math.min(nextCount, VISIBLE_LEGEND_LIMIT, regularSegmentLegends.length));
        };

        updateVisibleChipCount();

        const resizeObserver = new ResizeObserver(updateVisibleChipCount);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [isLegendExpanded, regularSegmentLegends]);

    useEffect(() => {
        setIsLegendExpanded(false);
        setIsOtherVendorExpanded(false);
    }, [regularSegmentLegends.length]);

    const toggleActiveSegment = (segmentKey: string) => {
        toggleSegmentSelection(segmentKey);
    };

    const handleBubbleClick = useCallback(
        (datum: { key?: string; segmentKey?: string; columnKey?: string }) => {
            if (!datum.segmentKey || !datum.columnKey || !datum.key) return;
            selectBubble({
                segmentKey: datum.segmentKey,
                columnKey: datum.columnKey,
                bubbleKey: datum.key,
            });
        },
        [selectBubble],
    );

    const chartDataset = useMemo(
        () =>
            chartRows
                .filter((point) => point.yFieldValue >= yZoomRange[0] && point.yFieldValue <= yZoomRange[1])
                .map((point) => {
                    const isActiveSegment = activeSegmentKey
                        ? point.segmentKey === activeSegmentKey || point.columnKey === activeSegmentKey
                        : false;
                    const shouldShowDetailedInfoLabel = isActiveSegment;
                    const shouldShowGrowthPctLabel = showHeatMap && !activeSegmentKey && point.growthText !== '-';
                    const isPinpointSelected = Boolean(
                        activeSegmentKey &&
                            point.segmentKey === activeSegmentKey &&
                            selectedColumnKey &&
                            point.columnKey === selectedColumnKey &&
                            (!selectedBubbleKey || point.key === selectedBubbleKey),
                    );
                    return {
                        ...point,
                        name: point.segmentLabel,
                        vendor: point.columnLabel,
                        segment: point.segmentLabel,
                        segmentKey: point.segmentKey,
                        columnKey: point.columnKey,
                        segmentBaseColor: point.segmentBaseColor,
                        highlightLabel: isActiveSegment ? point.segmentLabel : '',
                        bubbleColor: showHeatMap ? getHeatMapBubbleColor(point.growthDirection) : point.bubbleColor,
                        bubbleLabel: '',
                        isPinpointSelected,
                        showBubbleInfoLabel: shouldShowDetailedInfoLabel,
                        showGrowthPctLabel: shouldShowGrowthPctLabel,
                        infoLabelValueText: point.volumeSalesText,
                        infoLabelMetric: showHeatMap && isActiveSegment ? 'Growth' : 'Avg.',
                        infoLabelMetricValue: showHeatMap && isActiveSegment ? point.growthText : point.avgText,
                        infoLabelMetricDirection: point.growthDirection,
                    };
                }),
        [activeSegmentKey, chartRows, selectedBubbleKey, selectedColumnKey, showHeatMap, yZoomRange],
    );

    const xTickLabels = useMemo(() => (rows ?? []).map((row: PricePackBubbleRow) => row.description), [rows]);
    const xRange = useMemo(() => ({ xMin: 1, xMax: Math.max(1, (rows ?? []).length) }), [rows]);
    const minScrollableChartWidth = useMemo(
        () => Math.max(MIN_CHART_BASE_WIDTH_PX, xTickLabels.length * MIN_X_AXIS_COL_GAP_PX),
        [xTickLabels.length],
    );
    const chartCanvasWidth = useMemo(
        () => `max(calc(100% + ${MIN_INITIAL_SCROLLABLE_EXTRA_WIDTH_PX}px), ${minScrollableChartWidth}px)`,
        [minScrollableChartWidth],
    );

    const shouldShowError = showError || ((contextIsError || !!contextError) && !contextLoading);
    const showPaginationLoader = isFetchingNextPage || isChartPaginatedLoading;
    const showPanelLoader = (loading || isChartPanelLoading) && !showPaginationLoader;

    const handleHorizontalScroll = useCallback(() => {
        const container = horizontalScrollRef.current;
        if (!container || contextLoading || !hasMorePages || isFetchingNextPageRef.current) return;

        const reachedExtremeRight =
            container.scrollLeft + container.clientWidth >= container.scrollWidth - PAGINATION_RIGHT_EDGE_THRESHOLD_PX;
        if (!reachedExtremeRight) return;

        scrollSnapshotBeforePaginationRef.current = {
            scrollLeft: container.scrollLeft,
            scrollWidth: container.scrollWidth,
        };
        isFetchingNextPageRef.current = true;
        setIsFetchingNextPage(true);
        void fetchNextPricePackArchitecturePage().finally(() => {
            isFetchingNextPageRef.current = false;
            setIsFetchingNextPage(false);
        });
    }, [contextLoading, fetchNextPricePackArchitecturePage, hasMorePages]);

    useLayoutEffect(() => {
        const snapshot = scrollSnapshotBeforePaginationRef.current;
        const container = horizontalScrollRef.current;
        if (!snapshot || !container) return;

        const widthGrowth = container.scrollWidth - snapshot.scrollWidth;
        const targetScrollLeft =
            widthGrowth > 0
                ? snapshot.scrollLeft + widthGrowth - PAGINATION_SCROLL_RETREAT_PX
                : snapshot.scrollLeft - PAGINATION_SCROLL_RETREAT_PX;
        container.scrollLeft = Math.max(0, targetScrollLeft);
        scrollSnapshotBeforePaginationRef.current = null;
        container.dispatchEvent(new Event('scroll'));
    }, [rows.length]);

    if (shouldShowError) {
        return (
            <div className={`relative ${CHART_PANEL_CLASS}`}>
                <div className="flex h-[725px] items-center justify-center">
                    <ErrorBox msg={contextError || errorMessage} />
                </div>
            </div>
        );
    }

    if (!showPanelLoader && (chartRows?.length ?? 0) === 0) {
        return (
            <div className={`relative ${CHART_PANEL_CLASS}`}>
                <h3 className="mb-3 text-[18px] font-normal leading-[34px] text-headerColor">{chartTitle}</h3>
                <div className={`flex ${CHART_AREA_HEIGHT_CLASS} flex-col items-center justify-center gap-2`}>
                    <div className="flex h-[61px] w-[96px] shrink-0 items-center justify-center">
                        <InboxOutlined customIndex={0} />
                    </div>
                    <div className="flex flex-col items-center gap-1 text-center">
                        <p className="m-0 text-base font-normal leading-[22px] text-black/45">
                            No data for selected configuration
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`relative ${CHART_PANEL_CLASS}`}>
            {showPanelLoader ? (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/80">
                    <Loader />
                </div>
            ) : null}
            <h3 className="mb-3 text-[18px] font-normal leading-[34px] text-headerColor">{chartTitle}</h3>

            {(rows?.length ?? 0) === 0 ? (
                <div className={CHART_AREA_HEIGHT_CLASS} />
            ) : (
                <>
                    <div
                        ref={legendContainerRef}
                        className={`mb-4 flex gap-2 ${isLegendExpanded ? 'items-start' : 'items-center'}`}
                    >
                        <div className="relative min-w-0 min-h-6 flex-1">
                            {!isLegendExpanded ? (
                                <>
                                    <div
                                        ref={legendMeasureRef}
                                        className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-3 opacity-0"
                                        aria-hidden
                                    >
                                        {regularSegmentLegends.map((segment) => (
                                            <LegendChip
                                                key={`measure-${segment.id}`}
                                                segment={segment}
                                                isActive={false}
                                                isDimmed={false}
                                                truncate
                                                measureOnly
                                            />
                                        ))}
                                    </div>
                                    <div
                                        ref={legendControlsMeasureRef}
                                        className="pointer-events-none absolute right-0 top-0 flex items-center gap-2 opacity-0"
                                        aria-hidden
                                    >
                                        <span
                                            ref={legendMoreMeasureRef}
                                            className="inline-flex shrink-0 whitespace-nowrap text-xs text-toolbarLabelGrey"
                                        >
                                            +99 more
                                        </span>
                                        <span
                                            data-legend-dropdown-measure
                                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[#E4E4E7] bg-white p-0"
                                        >
                                            <HeaderSortDescChevron color={colorConstants.blackTitle} rotate={0} />
                                        </span>
                                    </div>
                                </>
                            ) : null}

                            <div
                                ref={legendRowRef}
                                className={
                                    isLegendExpanded
                                        ? 'flex min-h-6 flex-wrap items-start gap-3 overflow-y-auto overflow-x-hidden pb-1'
                                        : 'flex min-h-6 items-center gap-3 overflow-hidden'
                                }
                                style={isLegendExpanded ? { maxHeight: LEGEND_EXPANDED_MAX_HEIGHT_PX } : undefined}
                            >
                                {(isLegendExpanded ? regularSegmentLegends : visibleSegments).map((segment) => {
                                    const isActive = activeSegmentKey === segment.id;
                                    const isDimmed = activeSegmentKey !== null && !isActive;
                                    return (
                                        <LegendChip
                                            key={segment.id}
                                            segment={segment}
                                            isActive={isActive}
                                            isDimmed={isDimmed}
                                            truncate={!isLegendExpanded}
                                            onClick={() => toggleActiveSegment(segment.id)}
                                        />
                                    );
                                })}

                                {otherVendorLegend && isLegendExpanded ? (
                                    <div className="flex shrink-0 items-start gap-4">
                                        <span
                                            aria-hidden
                                            className="h-[15px] w-px shrink-0"
                                            style={{ backgroundColor: LEGEND_DIVIDER_COLOR }}
                                        />
                                        <div className="flex shrink-0 items-start gap-1">
                                            <button
                                                type="button"
                                                className={`m-0 flex shrink-0 cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-xs leading-none transition-opacity ${
                                                    activeSegmentKey === otherVendorLegend.key
                                                        ? 'font-semibold text-toolbarLabelGrey'
                                                        : 'text-toolbarLabelGrey'
                                                }`}
                                                style={{
                                                    opacity:
                                                        activeSegmentKey !== null &&
                                                        activeSegmentKey !== otherVendorLegend.key
                                                            ? 0.45
                                                            : 1,
                                                }}
                                                onClick={() => toggleActiveSegment(otherVendorLegend.key)}
                                                title={otherVendorLegend.label}
                                            >
                                                <LegendColorDot color={OTHER_SEGMENT_DOT_COLOR} />
                                                <span className="whitespace-nowrap">
                                                    {`${otherVendorLegend.label} (${otherVendorLegend.chips.length})`}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className="flex size-[18px] shrink-0 cursor-pointer items-start justify-center rounded border border-white bg-transparent p-0 mt-1"
                                                onClick={() => setIsOtherVendorExpanded((previous) => !previous)}
                                                aria-expanded={isOtherVendorExpanded}
                                                aria-label={
                                                    isOtherVendorExpanded
                                                        ? 'Collapse other vendor members'
                                                        : 'Expand other vendor members'
                                                }
                                            >
                                                <HeaderSortDescChevron
                                                    color={colorConstants.blackTitle}
                                                    rotate={isOtherVendorExpanded ? 270 : 90}
                                                />
                                            </button>
                                        </div>
                                        {isOtherVendorExpanded ? (
                                            <div className="flex max-w-[420px] items-center gap-4 overflow-x-auto pb-1">
                                                {otherVendorLegend.chips.map((chip) => (
                                                    <LegendChip
                                                        key={chip.id}
                                                        segment={chip}
                                                        isActive={false}
                                                        isDimmed={false}
                                                        truncate={!isLegendExpanded}
                                                        displayOnly
                                                    />
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {hasLegendOverflow ? (
                            <div className="flex h-6 shrink-0 items-center gap-2">
                                {!isLegendExpanded ? (
                                    <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap text-xs leading-none text-toolbarLabelGrey">
                                        {`+${hiddenLegendCount} more`}
                                    </span>
                                ) : null}
                                <button
                                    type="button"
                                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[#E4E4E7] bg-white p-0"
                                    onClick={() => setIsLegendExpanded((previous) => !previous)}
                                    aria-expanded={isLegendExpanded}
                                    aria-label={isLegendExpanded ? 'Collapse legend' : 'Expand legend'}
                                >
                                    <HeaderSortDescChevron
                                        color={colorConstants.blackTitle}
                                        rotate={isLegendExpanded ? 180 : 0}
                                    />
                                </button>
                            </div>
                        ) : null}
                    </div>

                    <div className={`relative flex ${CHART_AREA_HEIGHT_CLASS} gap-3`}>
                        {showPaginationLoader ? (
                            <div
                                className="absolute inset-0 z-20 flex cursor-wait items-center justify-center bg-white/45"
                                aria-live="polite"
                                aria-label="Loading more chart data"
                                aria-busy="true"
                            >
                                <Spin size="small" />
                            </div>
                        ) : null}
                        <VerticalRangeSlider
                            min={yAxisBounds.min}
                            max={yAxisBounds.max}
                            value={yZoomRange}
                            onChange={setYZoomRange}
                            formatLabel={formatRangeLabel}
                            disabled={isYZoomDisabled}
                        />

                        <div
                            ref={horizontalScrollRef as RefObject<HTMLDivElement>}
                            onScroll={handleHorizontalScroll}
                            className="relative flex-1 overflow-x-auto rounded bg-[#F9F9F9] pb-5"
                        >
                            <div className="relative h-full min-h-[440px]" style={{ width: chartCanvasWidth }}>
                                <BubbleScatterChart
                                    chartContainer={CHART_CONTAINER_ID}
                                    height={500}
                                    dataset={chartDataset}
                                    xAxisTitle={xAxisTitle}
                                    yAxisTitle={yAxisTitle}
                                    stroke="#d9d9d9"
                                    xRange={xRange}
                                    yRange={{ xMin: yZoomRange[0], xMax: yZoomRange[1] }}
                                    xTickLabels={xTickLabels}
                                    activeSegmentId={activeSegmentKey}
                                    segmentHighlightConfig={segmentHighlightConfig}
                                    onBubbleClick={handleBubbleClick}
                                    onChartBackgroundClick={clearSelection}
                                    loading={false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* <div className="mt-3 flex items-center justify-between text-xs text-toolbarLabelGrey">
                <span>{yAxisTitle}</span>
                <span>{xAxisTitle}</span>
            </div> */}
                </>
            )}
        </div>
    );
};

export default PackArchitectureChartWithControls;
